// src/components/PaymentGateway.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null);

// Detect dev/localhost and mock toggle
function isDevHost() {
  try { return typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'); } catch { return false; }
}
function queryFlag(name) {
  try { return new URLSearchParams(window.location.search).get(name); } catch { return null; }
}
const devMockEnabled = (process.env.NODE_ENV !== 'production') && (queryFlag('useMockPay') === '1' || process.env.REACT_APP_ENABLE_DEV_PAYMENT_MOCK !== '0');

function MockPaymentElement({ onMockPay, onCancel, isLoading, priceLabel }) {
  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: '1rem', border: '1px solid #e5e7eb' }}>
      <div style={{ marginBottom: '0.75rem' }}>
        <label style={{ display: 'block', fontSize: 12, color: '#64748b' }}>Card number</label>
        <input disabled={isLoading} placeholder="4242 4242 4242 4242" style={field} />
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#64748b' }}>Expiry</label>
          <input disabled={isLoading} placeholder="12 / 34" style={field} />
        </div>
        <div style={{ width: 120 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#64748b' }}>CVC</label>
          <input disabled={isLoading} placeholder="123" style={field} />
        </div>
        <div style={{ width: 180 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#64748b' }}>ZIP</label>
          <input disabled={isLoading} placeholder="94103" style={field} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button onClick={onCancel} style={{ background: '#fff', border: '1px solid #e5e7eb', padding: '0.5rem 0.75rem', borderRadius: 6 }}>Cancel</button>
        <button onClick={onMockPay} disabled={isLoading} style={{ background: '#4f46e5', color: '#fff', border: 'none', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
          {isLoading ? 'Processing…' : `Pay ${priceLabel}`}
        </button>
      </div>
    </div>
  );
}

function GatewayInner({ onPaymentSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    const result = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    setSubmitting(false);
    if (result.error) {
      // eslint-disable-next-line no-alert
      alert(result.error.message);
      return;
    }
    const intent = result.paymentIntent;
    onPaymentSuccess && onPaymentSuccess({ id: intent?.id, amount_received: intent?.amount_received });
  }, [stripe, elements, onPaymentSuccess]);

  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: '1rem' }}>
      <PaymentElement />
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button onClick={onCancel} style={{ background: '#fff', border: '1px solid #e5e7eb', padding: '0.5rem 0.75rem', borderRadius: 6 }}>Cancel</button>
        <button onClick={handleSubmit} disabled={!stripe || isSubmitting} style={{ background: '#4f46e5', color: '#fff', border: 'none', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
          {isSubmitting ? 'Processing…' : 'Pay' }
        </button>
      </div>
    </div>
  );
}

// Accept devMockOverride to force mock UI from frontend toggle
const PaymentGateway = ({
  onPaymentSuccess,
  onCancel,
  currentTier = 'free',
  targetTier = 'premium',
  userId = 'anonymous',
  currency = 'usd',
  devMockOverride = false
}) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const hasStripe = Boolean(stripePromise);

  const priceLabel = useMemo(() => (
    targetTier === 'premium' ? (currentTier === 'enhanced' ? '$14.99' : '$39.99') : '$24.99'
  ), [currentTier, targetTier]);

  useEffect(() => {
    let isMounted = true;
    async function createIntent() {
      if (devMockOverride) { setClientSecret(null); return; }
      setLoading(true);
      try {
        const res = await fetch('/api/create-payment-intent.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ currency, userId, currentTier, targetTier })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to create payment intent');
        if (isMounted) setClientSecret(data.clientSecret);
      } catch (e) {
        if (isMounted) setClientSecret(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    createIntent();
    return () => { isMounted = false; };
  }, [currency, userId, currentTier, targetTier, devMockOverride]);

  const elementsOptions = useMemo(() => (clientSecret ? ({ clientSecret }) : undefined), [clientSecret]);

  // Dev mocked PaymentElement UI when forced via toggle or dev env flag
  if (devMockOverride || (isDevHost() && devMockEnabled)) {
    return (
      <MockPaymentElement
        onCancel={onCancel}
        isLoading={isLoading}
        priceLabel={priceLabel}
        onMockPay={() => onPaymentSuccess && onPaymentSuccess({ id: 'mock_intent', amount_received: currentTier === 'enhanced' && targetTier === 'premium' ? 1499 : (targetTier === 'enhanced' ? 2499 : 3999) })}
      />
    );
  }

  if (!hasStripe || !clientSecret) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem' }}>
        <p style={{ marginTop: 0, color: '#64748b' }}>{isLoading ? 'Preparing checkout…' : 'Test mode: Stripe not configured. Use mock payment.'}</p>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={onCancel} style={{ background: '#fff', border: '1px solid #e5e7eb', padding: '0.5rem 0.75rem', borderRadius: 6 }}>Cancel</button>
          <button onClick={() => onPaymentSuccess && onPaymentSuccess({ id: 'mock_intent', amount_received: currentTier === 'enhanced' && targetTier === 'premium' ? 1499 : (targetTier === 'enhanced' ? 2499 : 3999) })} style={{ background: '#4f46e5', color: '#fff', border: 'none', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
            Mock Pay {priceLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <GatewayInner onPaymentSuccess={onPaymentSuccess} onCancel={onCancel} />
    </Elements>
  );
};

const field = { width: '100%', padding: '0.5rem 0.625rem', border: '1px solid #e5e7eb', borderRadius: 6 };

export default PaymentGateway;
